import { PointColorHueMaxShift } from "@/core/hsl-point-color"
import { useTranslation } from "react-i18next"
import { PointColorHSL, emptyPointColor } from "@/core/types"
import { HSVToRGB, RGBToHSV } from "@/core/utils/color-conversion"
import clsx from "clsx"
import { useEffect, useRef, useState } from "react"
import { usePointerDrag } from "@/utils/usePointerDrag"
import { clamp } from "@/utils/math"
import {
	getHSVRelativeShiftInRange,
	getHueShiftRange,
	getLumShiftRange,
	getSatShiftRange,
} from "./utils"
import { vec3 } from "gl-matrix"

export const HSLColorPicker = (props: {
	pointColor: PointColorHSL
	onUpdateColor: (pointColor: PointColorHSL) => void
	onUpdateColorEnded: () => void
	className?: string
}) => {
	const { t } = useTranslation("components")
	const pointColor = props.pointColor

	const canvasRef = useRef<HTMLCanvasElement>(null)
	const [draggingSatShift, setDraggingSatShift] = useState<number | null>(null)
	const [draggingLumShift, setDraggingLumShift] = useState<number | null>(null)

	/* General */

	const initialHSV = RGBToHSV(pointColor.color)

	const hueRange = getHueShiftRange(initialHSV)
	const satRange = getSatShiftRange(initialHSV)
	const lumRange = getLumShiftRange(initialHSV)

	const relativeHSVActual = getHSVRelativeShiftInRange(initialHSV)
	const relativeInitialHSV: vec3 = [
		relativeHSVActual[0],
		1 - relativeHSVActual[1],
		1 - relativeHSVActual[2],
	]

	/* Actions */

	// const resetLum = () => {
	// 	props.onUpdateColor({
	// 		...props.pointColor,
	// 		lumShift: 0,
	// 	})
	// }

	// const resetHueSat = () => {
	// 	props.onUpdateColor({
	// 		...props.pointColor,
	// 		hueShift: 0,
	// 		satShift: 0,
	// 	})
	// }

	const updateLumFromSlider = (position: [number, number], currentTarget: HTMLDivElement) => {
		const rect = currentTarget.querySelector("#lum-slider")?.getBoundingClientRect()
		const pointerRect = currentTarget.querySelector("#lum-position")?.getBoundingClientRect()

		if (!rect) {
			return
		}

		const height = rect.height
		const y = position[1] - rect.top - (pointerRect?.height ?? 0) / 2.0

		const rawLumShift = 2.0 * clamp(y / height, 0.0, 1.0) - 1.0

		let lumShift = 0

		if (clamp(y / height, 0, 1) <= relativeInitialHSV[2]) {
			lumShift = 1.0 - y / (relativeInitialHSV[2] * height)
		} else {
			lumShift = -(1.0 - (height - y) / ((1.0 - relativeInitialHSV[2]) * height))
		}

		props.onUpdateColor({
			...props.pointColor,
			lumShift: clamp(lumShift, -1, 1),
		})

		setDraggingLumShift((rawLumShift + 1.0) / 2.0)
	}

	const updateHueSatFromMap = (position: [number, number], currentTarget: HTMLDivElement) => {
		const innerRect = currentTarget.querySelector("#hue-sat-map")?.getBoundingClientRect()
		const pointerRect = currentTarget
			.querySelector("#hue-sat-position")
			?.getBoundingClientRect()

		if (!innerRect) {
			return
		}

		const { width, height } = innerRect

		const x = position[0] - innerRect.left - (pointerRect?.width ?? 0) / 2.0
		const y = position[1] - innerRect.top - (pointerRect?.height ?? 0) / 2.0

		const rawHueShift = 2.0 * clamp(x / width, 0.0, 1.0) - 1.0
		const rawSatShift = 2.0 * clamp(y / height, 0.0, 1.0) - 1.0

		let satShift = 0

		if (clamp(y / height, 0, 1) <= relativeInitialHSV[1]) {
			satShift = 1.0 - y / (relativeInitialHSV[1] * height)
		} else {
			satShift = -(1.0 - (height - y) / ((1.0 - relativeInitialHSV[1]) * height))
		}

		props.onUpdateColor({
			...props.pointColor,
			hueShift: rawHueShift,
			satShift: clamp(satShift, -1, 1),
		})

		setDraggingSatShift((rawSatShift + 1.0) / 2.0)
	}

	/* Dragging */

	const bindLumDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			updateLumFromSlider([e.clientX, e.clientY], e.currentTarget)
		},
		onPointerUp() {
			setDraggingLumShift(null)
			props.onUpdateColorEnded()
		},
		onDrag(drag) {
			updateLumFromSlider([drag.x, drag.y], drag.currentTarget)
		},
	})

	const bindHueSatDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			updateHueSatFromMap([e.clientX, e.clientY], e.currentTarget)
		},
		onPointerUp() {
			setDraggingSatShift(null)
			props.onUpdateColorEnded()
		},
		onDrag(drag) {
			updateHueSatFromMap([drag.x, drag.y], drag.currentTarget)
		},
	})

	/* Rendering hue-sat map */

	useEffect(() => {
		const canvas = canvasRef.current
		const ctx = canvasRef.current?.getContext("2d")

		if (!canvas || !ctx) {
			return
		}

		const parent = canvas.parentElement
		const width = parent?.clientWidth ?? canvas.width
		const height = parent?.clientHeight ?? canvas.height

		const hueStart = initialHSV[0] - PointColorHueMaxShift
		const hueEnd = initialHSV[0] + PointColorHueMaxShift

		const displayVal = Math.max(initialHSV[2], 50)

		for (let x = 0; x < width; x++) {
			const hueProgress = x / width
			const hue = ((hueEnd - hueStart) * hueProgress + hueStart + 360) % 360

			for (let y = 0; y < height; y++) {
				const satProgress = 1.0 - y / height
				const sat = (satRange[1] - satRange[0]) * satProgress + satRange[0]

				const [r, g, b] = HSVToRGB([hue, sat, displayVal])
				ctx.fillStyle = `rgb(${r * 255}, ${g * 255}, ${b * 255})`
				ctx.fillRect(x, y, 1, 1)
			}
		}
	}, [props.pointColor.color])

	const renderHueSat = () => {
		const relativeFinalHueSat: [number, number] = [
			(initialHSV[0] + pointColor.hueShift * PointColorHueMaxShift - hueRange[0]) /
				(hueRange[1] - hueRange[0]),
			0,
		]

		if (draggingSatShift === null) {
			if (pointColor.satShift >= 0) {
				relativeFinalHueSat[1] =
					relativeInitialHSV[1] * (1.0 - Math.abs(pointColor.satShift))
			} else {
				relativeFinalHueSat[1] =
					relativeInitialHSV[1] +
					(1.0 - relativeInitialHSV[1]) * Math.abs(pointColor.satShift)
			}
		} else {
			relativeFinalHueSat[1] = draggingSatShift
		}

		return (
			<>
				<canvas className="absolute inset-0" ref={canvasRef}></canvas>
				<div id="hue-sat-map" className="absolute bottom-4 left-0 right-4 top-0">
					{/* Default position */}
					<div
						style={{
							top: `${relativeInitialHSV[1] * 100}%`,
							left: `${relativeInitialHSV[0] * 100}%`,
						}}
						className="absolute mx-1 mt-1 size-2 rounded-full border border-white bg-black/50"
					></div>
					{/* Current position */}
					<div
						id="hue-sat-position"
						style={{
							top: `${relativeFinalHueSat[1] * 100}%`,
							left: `${relativeFinalHueSat[0] * 100}%`,
						}}
						className="absolute size-4 rounded-full border-2 border-white"
					></div>
				</div>
			</>
		)
	}

	const renderLum = () => {
		const [r, g, b] = HSVToRGB([initialHSV[0], initialHSV[1], lumRange[0]])
		const start = `rgb(${r * 255}, ${g * 255}, ${b * 255})`
		const [r2, g2, b2] = HSVToRGB([initialHSV[0], initialHSV[1], lumRange[1]])
		const end = `rgb(${r2 * 255}, ${g2 * 255}, ${b2 * 255})`

		const lumBackground = `linear-gradient(0deg, ${start}, ${end})`

		let relativeFinalLuminance = 0

		if (draggingLumShift === null) {
			if (pointColor.lumShift >= 0) {
				relativeFinalLuminance =
					relativeInitialHSV[2] * (1.0 - Math.abs(pointColor.lumShift))
			} else {
				relativeFinalLuminance =
					relativeInitialHSV[2] +
					(1.0 - relativeInitialHSV[2]) * Math.abs(pointColor.lumShift)
			}
		} else {
			relativeFinalLuminance = draggingLumShift
		}

		return (
			<div
				style={{
					background: lumBackground,
				}}
				className="relative size-full"
			>
				<div
					id="lum-slider"
					className="absolute inset-x-0 bottom-4 top-0 flex w-full flex-row justify-center"
				>
					{/* Default position */}
					<div
						style={{ top: `${relativeInitialHSV[2] * 100}%` }}
						className="absolute mt-1 size-2 rounded-full border border-white bg-white/50 dark:bg-black/50"
					></div>
					{/* Current position */}
					<div
						id="lum-position"
						style={{
							top: `${relativeFinalLuminance * 100}%`,
						}}
						className="absolute size-4 rounded-full border-2 border-white bg-white/50 dark:bg-black/50"
					></div>
				</div>
			</div>
		)
	}

	const renderUsageNote = () => {
		return (
			<div className="flex size-full items-center justify-center text-center text-xs opacity-75">
				{t("hslColorPicker.useDropperPrompt")}
			</div>
		)
	}

	return (
		<div className={clsx(`box-border flex h-32 gap-2`, props.className)}>
			{/* Hue Saturation map */}
			<div
				className="relative size-full cursor-pointer overflow-hidden rounded border border-black/50 bg-white/10"
				{...bindHueSatDrag}
			>
				{pointColor !== emptyPointColor && renderHueSat()}
				{pointColor === emptyPointColor && renderUsageNote()}
			</div>
			{/* Luminosity slider */}
			<div
				className="h-full w-6 cursor-pointer overflow-hidden rounded border border-black/50 bg-white/10"
				{...bindLumDrag}
			>
				{pointColor !== emptyPointColor && renderLum()}
			</div>
		</div>
	)
}
